"use client";

import { RetrievedPartner, RetrievedPartnerFullProfile, RetrievedProductCategory } from "@/lib/sanity/responseTypes";
import { useEffect, useRef, useState } from "react";
import NavigationFilters from "../NavigationFilters";
import { useTranslations } from "next-intl";
import AddressDisplay from "../ecommerce/address-display";

import Phone from "@/components/icons/phone.svg";
import Envelope from "@/components/icons/envelope-outline.svg";
import Globe from "@/components/icons/globe-alt.svg";
import ArrowRight from "@/components/icons/arrow-right.svg";
import { sanityClient } from "@/lib/sanity";
import { GET_FULL_PARTNER_PROFILE } from "@/lib/sanity/queries";
import PartnerCatalogue from "./PartnerCatalogue";
import { useRouter } from "@/i18n/navigation";
import { AllProductsReturnType } from "@/lib/server/shop/products/all-products";

interface PartnersViewProps {
  partners: RetrievedPartner[];
  //productCategories: RetrievedProductCategory[];
  allProducts: AllProductsReturnType;
}

export default function PartnersView(props: PartnersViewProps) {
  const { productsCategories: productCategories } = props.allProducts;
  const t = useTranslations('Standorte');
  const router = useRouter();

  const partnerModalRef = useRef<HTMLDialogElement | null>(null);

  const [partnersSlice, setPartnersSlice] = useState<RetrievedPartner[]>([]);
  const [countriesItems, setCountriesItems] = useState<{
    code: string;
    label: string;
    doNotTranslate?: boolean;
    active: boolean;
    path?: string;
    onClick?: () => void;
    imageUrl?: string;
    imageClassNames?: string;
  }[]>([]);
  const [currentCountry, setCurrentCountry] = useState<{
    code: string;
    label: string;
    doNotTranslate?: boolean;
    active: boolean;
    path?: string;
    onClick?: () => void;
    imageUrl?: string;
    imageClassNames?: string;
  } | null>(null);
  const [currentSelectedPartnerId, setCurrentSelectedPartnerId] = useState<string | null>(null);
  const [currentSelectedPartner, setCurrentSelectedPartner] = useState<RetrievedPartner | null>(null);
  const [currentSelectedPartnerFullProfile, setCurrentSelectedPartnerFullProfile] = useState<RetrievedPartnerFullProfile | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  function setCountryByCode(code: string) {
    if (code === 'all') {
      setCurrentCountry(null);
    } else {
      setCurrentCountry(countriesItems.find(c => c.code === code)!);
    }
  }

  useEffect(() => {
    let itemsList: {
      code: string;
      label: string;
      doNotTranslate?: boolean;
      active: boolean;
      path?: string;
      onClick?: () => void;
      imageUrl?: string;
      imageClassNames?: string;
    }[] = [
        {
          code: 'all',
          label: 'all',
          active: !currentCountry,
          onClick: () => setCountryByCode('all'),
        },
        ...(['ch', 'de', 'it', 'at', 'si'].map(countryCode => ({
          code: countryCode,
          label: `country_${countryCode}`,
          active: currentCountry?.code === countryCode,
          imageUrl: `/flags/${countryCode}.svg`,
          onClick: () => setCountryByCode(countryCode),
        }))),
      ];

    setCountriesItems(itemsList);
  }, [props.partners, currentCountry]);

  useEffect(() => {
    if (!currentCountry) {
      setPartnersSlice(props.partners);
    } else {
      setPartnersSlice(props.partners.filter(p => p.countryCode.toLowerCase() === currentCountry?.code));
    }
  }, [currentCountry]);

  useEffect(() => {
    if (currentSelectedPartnerId) {
      let newSelectedPartner = props.partners.find(p => p._id === currentSelectedPartnerId)!
      if (newSelectedPartner.invento) {
        router.push('/kategorie');
      } else {
        setCurrentSelectedPartner(newSelectedPartner);
      }
    } else {
      setCurrentSelectedPartner(null);
    }
  }, [currentSelectedPartnerId])

  useEffect(() => {
    if (!currentSelectedPartner) {
      return;
    }

    sanityClient.fetch(GET_FULL_PARTNER_PROFILE, { id: currentSelectedPartner?._id })
      .then(res => {
        console.log(`Received response: ${JSON.stringify(res, undefined, '  ')}`)
        let fullProfile: RetrievedPartnerFullProfile = res[0] as RetrievedPartnerFullProfile;
        setCurrentSelectedPartnerFullProfile(fullProfile);
      })
      .catch(err => {
        console.error(`Error while retrieving full partner info`, err);
      })
  }, [currentSelectedPartner]);

  function partnerModalHandler(partnerId: string) {
    return async () => {
      setCurrentSelectedPartnerId(partnerId);
      setTimeout(() => partnerModalRef.current?.showModal(), 200);
    }
  }

  function closeModal() {
    setCurrentSelectedPartnerFullProfile(null);
  }

  return (
    <div className="flex flex-col gap-y-[20px] font-[Outfit]">
      {/*<NavigationFilters items={countriesItems} translationCollection={'Standorte'} />*/}
      <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 4xl:grid-cols-4 p-2.5 gap-5">
        {partnersSlice.map((p, i) => {
          return (
            <div key={`country-${i}`} className="flex flex-col p-4 gap-2.5 border-accent border-1 rounded-2xl">
              <div className="w-full font-bold text-sm leading-5 basis-5">
                {t(`country_${p.countryCode.toLowerCase()}`)}
              </div>
              <div className="w-full font-bold text-[32px] text-primary leading-[120%] basis-10 pb-2.5">
                {p.name}{p.invento ? ` (${t('headquarters')})` : ''}
              </div>
              <div className="flex-auto flex h-full max-h-[272px] items-center justify-center">
                {p.imageUrl && p.imageUrl.length > 0 ? (
                  <div className="flex-auto flex items-center justify-center max-h-[272px]">
                    <img
                      src={`${p.imageUrl}?w=1200&h=1200`}
                      alt={p.name}
                      className="max-h-[272px] max-w-full w-auto h-auto object-contain"
                    />
                  </div>
                ) : (
                  <div className="h-full max-w-full aspect-square flex flex-col justify-center items-center">
                    <div className="w-full aspect-square border-1 border-black text-center align-middle flex flex-col justify-center items-center">
                      <div>
                        {t('picture_missing')}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 flex-1 pt-2.5">
                <div className="flex flex-col gap-2.5">
                  <div className="text-2xl leading-5 font-bold">
                    {t('location')}
                  </div>
                  <AddressDisplay className="text-base leading-[18px] md:leading-[30px]"
                    company={p.name}
                    addressLine1={p.addressLine1}
                    addressLine2={p.addressLine2}
                    zip={p.zip}
                    city={p.town}
                    country={p.country}
                  />
                </div>
                <div className="flex flex-col gap-2 md:gap-4 md:h-[164px]">
                  <div className="text-2xl leading-5 h-5 font-bold">
                    {t('contact')}
                  </div>
                  {p.phone && (<a href={`tel:${p.phone}`} className="font-semibold text-base leading-5 w-full flex flex-row gap-1.5 items-center text-ellipsis overflow-hidden">
                    <div className="inline-block w-[18px] h-[18px]">
                      <Phone className="inline-block w-[18px] h-[18px] stroke-black" />
                    </div>
                    {p.phone}
                  </a>)}
                  {p.email && (<a href={`mailto:${p.email}`} className="font-semibold text-base leading-5 w-full flex flex-row gap-1.5 items-center text-ellipsis overflow-hidden">
                    <div className="inline-block w-[18px] h-[18px]">
                      <Envelope className="inline-block w-[18px] h-[18px] stroke-black" />
                    </div>
                    {p.email}
                  </a>)}
                  {p.website && (<a href={p.website} className="font-semibold text-base leading-5 w-full flex flex-row gap-1.5 items-center text-ellipsis overflow-hidden" target="_blank">
                    <div className="inline-block w-[18px] h-[18px]">
                      <Globe className="w-[18px] h-[18px] stroke-black" />
                    </div>
                    {p.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                  </a>)}
                </div>
              </div>
              <div className="flex flex-row justify-center">
                <button className="btn btn-secondary text-white rounded-full" onClick={partnerModalHandler(p._id)}>
                  {t('show_products')}
                  <ArrowRight className="h-[18px] w-[18px]  stroke-white" />
                </button>
              </div>
            </div>
          )
        })}
      </div>
      <dialog id="partner_assortment_modal" className="modal" ref={partnerModalRef}>
        <div className="modal-box w-11/12 max-w-7xl">
          <h3 className="font-bold text-lg font-[Outfit]">{t('invento_products')}</h3>
          <form method="dialog">
            {/* if there is a button in form, it will close the modal */}
            <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">✕</button>
          </form>
          {currentSelectedPartnerFullProfile && (
            <>
              <PartnerCatalogue products={currentSelectedPartnerFullProfile.products} catalogue={props.allProducts} sellsAllProducts={currentSelectedPartnerFullProfile.sellsAll} />
              {currentSelectedPartnerFullProfile.website && currentSelectedPartnerFullProfile.website.length > 0 && (
                <div className="flex flex-col items-end">
                  <a href={currentSelectedPartnerFullProfile.website}>
                    <button className="btn btn-secondary text-white rounded-full">
                      {t('to_partner_website')}
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </a>
                </div>
              )}
            </>
          )}
        </div>
      </dialog>
    </div>
  )
}
